<template>
  <div>
    <span v-if="minQtyErrorPresent" v-html="bp('skuGridErrorMessage')"></span>

    <table class="b2b-grid b2b-grid--collapse is-compact" qaid="saved-list-sku-grid">
      <!-- SKU Grid Headers -->
      <thead class="b2b-grid__header">
        <tr>
          <th class="b2b-grid__cell b2b-grid__head w-4">
            <span :qaid="`sku-grid-checkbox-${baseProduct.baseProduct}`" @click.prevent.stop="checkAll">
              <grid-checkbox :label="baseProduct.baseProduct" :checked="isAllChecked" />
            </span>
          </th>
          <th class="b2b-grid__cell b2b-grid__head w-5" qaid="sku-grid.icon"></th>

          <th class="b2b-grid__cell b2b-grid__head w-5" qaid="sku-grid.note"></th>

          <th class="b2b-grid__cell b2b-grid__head uppercase" qaid="sku-grid.sku" v-html="bp('sku')"></th>

          <th class="b2b-grid__cell b2b-grid__head uppercase" qaid="sku-grid.upc-ean" v-html="bp('upc')"></th>

          <th
            class="b2b-grid__cell b2b-grid__head lg:text-right"
            qaid="sku-grid.allocated"
            v-html="bp('allocated')"
          ></th>

          <th
            class="b2b-grid__cell b2b-grid__head lg:text-right"
            qaid="sku-grid.backordered"
            v-html="bp('backordered')"
          ></th>

          <!-- Variable variant headers -->
          <template v-for="(variant, variantIndex) in variantHeaders">
            <th :key="variantIndex" class="b2b-grid__cell b2b-grid__head">
              <span v-if="variant.name === 'Size'" qaid="sku-grid.size" v-html="bp('size')"></span>

              <span v-else-if="variant.name === 'Color'" v-html="bp('color')"></span>

              <span v-else>
                <!-- Unknown variant - Printing the raw name -->
                {{ variant.name }}
              </span>
            </th>
          </template>

          <th
            v-if="!consumerFriendlyMode"
            class="b2b-grid__cell b2b-grid__head"
            qaid="sku-grid.order-price"
            v-html="bp('price')"
          ></th>

          <th qaid="sku-grid.list" class="b2b-grid__cell b2b-grid__head lg:text-right">
            <i class="md-18">list</i>
          </th>

          <th class="b2b-grid__cell b2b-grid__head" qaid="sku-grid.package-quantity" v-html="bp('qty')"></th>

          <th
            v-if="!consumerFriendlyMode"
            class="b2b-grid__cell b2b-grid__head text-right"
            qaid="sku-grid.subtotal"
            v-html="bp('subtotal')"
          ></th>

          <th class="b2b-grid__cell b2b-grid__head w-5" qaid="sku-grid.trash"></th>
        </tr>
      </thead>

      <!-- SKU Grid Body/Rows -->
      <tbody>
        <!-- For each Product/Row -->
        <template v-for="(product, productIndex) in sortedProducts">
          <cart-product-grid-row
            :key="productIndex"
            :checkbox-list="checkboxList"
            :update-check-all="updateCheckAll"
            :add-to-checkbox-list="addToCheckboxList"
            :remove-from-checkbox-list="removeFromCheckboxList"
            :base-product="baseProduct"
            :base-product-index="baseProductIndex"
            :base-properties="baseProperties"
            :product="product"
            :min-qty-error="minQtyFailList && minQtyFailList[product.sku] ? true : false"
            :min-qty-error-msg="minQtyFailList[product.sku] ? minQtyFailList[product.sku] : null"
            :row-index="productIndex"
            @open-confirm-delete-dialog="openConfirmDeleteDialog"
            @send-cart-update="sendCartUpdate"
          />
        </template>
      </tbody>
    </table>

    <el-dialog
      :visible.sync="dialogVisible"
      :title="bp('confirm')"
      :center="true"
      :close-on-press-escape="false"
      :close-on-click-modal="false"
      :z-index="16000011"
      :close-dialog-tooltip="bp('closeDialogToolTip')"
      @close="resetDeleteData"
    >
      <template slot="footer">
        <div class="buttons buttons--center">
          <trek-button alert qaid="confirm-delete-button" @click="confirmDelete">
            <span v-html="bp('remove')"></span>
          </trek-button>
          <trek-button secondary qaid="not-remove-button" @click="dialogVisible = false">
            <span v-html="bp('doNotRemove')"></span>
          </trek-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import Vue from 'vue';
import {mapActions, mapMutations, mapGetters} from 'vuex';
import TrekButton from '@/components/TrekButton';
import ElDialog from '../Dialog.vue';
import GridCheckbox from '../grid/GridCheckbox';
import get from 'lodash/get';
import reduce from 'lodash/reduce';
import CartProductGridRow from '@/components/cart/CartProductGridRow';
import {SortedProducts} from '@/components/containers/bulk-product/mixins/sorted-products';

export default {
  components: {TrekButton, ElDialog, GridCheckbox, CartProductGridRow},

  mixins: [SortedProducts],
  props: {
    cartId: {
      type: String,
      default: '',
    },
    baseProperties: {
      type: Array,
      default: () => [],
    },
    baseProduct: {
      type: Object,
      default: () => {},
    },
    baseProductIndex: {
      type: Number,
      default: 0,
    },
    partType: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      dialogVisible: false,
      itemToDeleteElement: null,
      itemDeleted: false,
      minQtyFailList: {},
      minQtyErrorPresent: false,
      isAllChecked: false,
      checkboxList: [],
    };
  },

  computed: {
    sortedProducts() {
      return this.getSortedProducts(this.baseProduct.entries);
    },

    variantHeaders() {
      const variantItem = this.baseProduct;
      const categoriesObject = get(variantItem, 'entries[0].variantValueCategories[0]');

      if (categoriesObject) {
        const categories = Object.keys(categoriesObject);
        let variants = [];

        categories.forEach(function(category) {
          if (variantItem.groupBy != category) {
            variants.push({name: category});
          }
        });

        return variants;
      }

      return null;
    },
    ...mapGetters('backend', ['consumerFriendlyMode']),
    ...mapGetters('cart', ['selectedProducts']),
  },

  watch: {
    selectedProducts(list) {
      if (
        list.length === 0 ||
        !list.some((product) =>
          this.baseProduct.entries.every((baseProduct) => baseProduct.productType === product.productType)
        )
      ) {
        this.isAllChecked = false;
        this.checkboxList = [];
      }
    },
  },

  methods: {
    addToCheckboxList(product) {
      if (!this.checkboxList.some((item) => item === product)) {
        this.checkboxList.push(product);
      }
    },
    removeFromCheckboxList(product) {
      if (this.checkboxList.some((item) => item === product)) {
        this.checkboxList = this.checkboxList.filter((item) => item !== product);
      }
    },
    checkAll() {
      this.isAllChecked = !this.isAllChecked;
      if (this.isAllChecked) {
        this.baseProduct.entries.forEach((item) => {
          if (!this.checkboxList.includes(item.sku)) {
            this.checkboxList.push(item.sku);
          }
        });
      } else {
        this.checkboxList = [];
      }
    },
    updateCheckAll() {
      if (this.checkboxList.length === this.baseProduct.entries.length) {
        this.isAllChecked = true;
      } else {
        this.isAllChecked = false;
      }
    },

    getProductQuantity(product) {
      return reduce(product.warehouses, (result, warehouse) => result + parseInt(warehouse.orderedQty), 0);
    },

    openConfirmDeleteDialog(element) {
      this.dialogVisible = true;
      this.itemToDeleteElement = element;
    },

    // This is called when the confirmation dialog closes
    resetDeleteData() {
      this.dialogVisible = false;

      if (!this.itemDeleted) {
        this.itemToDeleteElement.value = this.itemToDeleteElement.dataset.initialValue;
      }

      this.itemDeleted = false;
      this.itemToDeleteElement = null;
    },

    confirmDelete() {
      this.dialogVisible = false;
      this.itemDeleted = true;
      const sku = this.itemToDeleteElement.dataset.sku;

      this.minQtyErrorPresent = false;
      this.minQtyFailList = {};

      this.updateCart([{sku: sku, qty: 0}], true)
        .then((response) => {
          let skus = (get(response, 'data.data.failedSKUs') || []).map((item) => item.sku);
          let messages = (get(response, 'data.data.failedSKUs') || []).map((item) => item.message.basePropertyValue);

          this.updateFailedSkus(skus, messages);
        })
        .finally(() => {
          this.fetchCart().then(() => {
            this.removeFromCheckboxList(sku);
            this.removeSelectedProduct(sku);
            if (this.checkboxList.length === this.baseProduct.entries.length && !this.isAllChecked) {
              this.isAllChecked = true;
            }
          });
        });
    },

    updateFailedSkus(skus, messages) {
      this.minQtyFailList = {};

      if (skus.length) this.minQtyErrorPresent = true;

      for (let i = 0; i < skus.length; i++) {
        Vue.set(this.minQtyFailList, skus[i], messages[i]);
      }
    },

    sendCartUpdate(cartData) {
      this.minQtyErrorPresent = false;
      this.minQtyFailList = {};

      this.updateCart([...cartData]).then((response) => {
        let skus = (get(response, 'data.data.failedSKUs') || []).map((item) => item.sku);
        let messages = (get(response, 'data.data.failedSKUs') || []).map((item) => item.message.basePropertyValue);

        this.updateFailedSkus(skus, messages);
      });
    },

    ...mapMutations('savedLists', ['setEntries']),

    ...mapActions('cart', ['removeProducts', 'undoRemove', 'updateCart', 'fetchCart', 'removeSelectedProduct']),
  },
};
</script>
